import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { SPARTACUS_UK_TIMEZONE } from 'src/constants/spartacus'

dayjs.extend(utc)
dayjs.extend(timezone)

const DATE_FORMAT = 'YYYY-MM-DD'
/** Inclusive UK calendar days in each north-star comparison window. */
export const NORTH_STAR_ROLLING_DAYS = 7

/**
 * Rolling last 7 UK days (through today) vs the previous 7 UK days.
 * Always a full week of signal for Crow’s Nest WoW.
 */
export function getNorthStarWeekOverWeekRanges(now = dayjs()) {
  const today = now.tz(SPARTACUS_UK_TIMEZONE)
  const currentTo = today.endOf('day')
  const currentFrom = today
    .subtract(NORTH_STAR_ROLLING_DAYS - 1, 'day')
    .startOf('day')
  const priorTo = currentFrom.subtract(1, 'day').endOf('day')
  const priorFrom = priorTo
    .subtract(NORTH_STAR_ROLLING_DAYS - 1, 'day')
    .startOf('day')

  return {
    timezone: SPARTACUS_UK_TIMEZONE,
    current: {
      fromDate: currentFrom.format(DATE_FORMAT),
      toDate: currentTo.format(DATE_FORMAT),
      from: currentFrom.toDate(),
      to: currentTo.toDate(),
      label: formatUkWeekRangeLabel(currentFrom, currentTo),
    },
    prior: {
      fromDate: priorFrom.format(DATE_FORMAT),
      toDate: priorTo.format(DATE_FORMAT),
      from: priorFrom.toDate(),
      to: priorTo.toDate(),
      label: formatUkWeekRangeLabel(priorFrom, priorTo),
    },
  }
}

export function formatUkWeekRangeLabel(from, to) {
  const start = dayjs(from).tz(SPARTACUS_UK_TIMEZONE)
  const end = dayjs(to).tz(SPARTACUS_UK_TIMEZONE)
  if (start.isSame(end, 'day')) {
    return start.format('D MMM YYYY')
  }
  if (start.isSame(end, 'month')) {
    return `${start.format('D')}–${end.format('D MMM YYYY')}`
  }
  return `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`
}

/**
 * Absolute + relative delta for week-over-week KPI.
 * @param {number} current
 * @param {number} prior
 */
export function buildWeekOverWeekDelta(current, prior) {
  const currentValue = Number(current) || 0
  const priorValue = Number(prior) || 0
  const absolute = currentValue - priorValue

  let percent = null
  if (priorValue > 0) {
    percent = Number(((absolute / priorValue) * 100).toFixed(1))
  } else if (currentValue > 0 && priorValue === 0) {
    percent = null // avoid ∞%; UI shows absolute only
  }

  return {
    current: currentValue,
    prior: priorValue,
    absolute,
    percent,
    direction: absolute > 0 ? 'up' : absolute < 0 ? 'down' : 'flat',
  }
}

export function formatWeekOverWeekDeltaLabel(delta) {
  if (!delta) return '—'
  const sign = delta.absolute > 0 ? '+' : ''
  const absPart = `${sign}${delta.absolute}`
  if (delta.percent == null) {
    if (delta.prior === 0 && delta.current > 0) {
      return `${absPart} vs prior 7 days (new)`
    }
    return `${absPart} vs prior 7 days`
  }
  const pctSign = delta.percent > 0 ? '+' : ''
  return `${absPart} (${pctSign}${delta.percent}%) vs prior 7 days`
}

/**
 * Map API totals into widget-facing shape.
 */
export function mapNorthStarTotals(totals = {}) {
  return {
    uniqueVenues: Number(totals.uniqueVenues) || 0,
    opportunities: Number(totals.opportunities) || 0,
    emailsSent: Number(totals.emailsSent) || 0,
    uniqueVenuesWithEmail: Number(totals.uniqueVenuesWithEmail) || 0,
  }
}

/**
 * Build presentation model for the north star widget.
 */
export function buildNorthStarWidgetModel({
  currentTotals,
  priorTotals,
  ranges,
  definition,
} = {}) {
  const current = mapNorthStarTotals(currentTotals)
  const prior = mapNorthStarTotals(priorTotals)
  const venuesDelta = buildWeekOverWeekDelta(
    current.uniqueVenues,
    prior.uniqueVenues,
  )
  const opportunitiesDelta = buildWeekOverWeekDelta(
    current.opportunities,
    prior.opportunities,
  )

  return {
    definition: definition || {
      metric: 'unique_free_venues_with_paywall_opportunity',
      description:
        'Free venues that hit a premium feature limit and created a paywall sales opportunity',
    },
    ranges,
    primary: {
      key: 'uniqueVenues',
      label: 'Free venues at paywall',
      value: current.uniqueVenues,
      priorValue: prior.uniqueVenues,
      delta: venuesDelta,
      deltaLabel: formatWeekOverWeekDeltaLabel(venuesDelta),
    },
    secondary: [
      {
        key: 'opportunities',
        label: 'Paywall opportunities',
        value: current.opportunities,
        priorValue: prior.opportunities,
        delta: opportunitiesDelta,
        deltaLabel: formatWeekOverWeekDeltaLabel(opportunitiesDelta),
      },
      {
        key: 'emailsSent',
        label: 'Sales emails sent',
        value: current.emailsSent,
        priorValue: prior.emailsSent,
      },
    ],
  }
}
